import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWinningWorksByCategory } from '../../Redux/Actions/Index';
import Swal from 'sweetalert2';
import { Layout, Row, Col, Tabs, Collapse } from 'antd';
import './Concurso.css';
import collageConcurso from '../../components/Assets/collageConcurso.jpeg';
import BASES_Y_CONDICIONES_TEXTO from '../../content/concursoBases';

const { TabPane } = Tabs;
const { Content } = Layout;
const { Panel } = Collapse;

const CATEGORY_BY_TAB = {
  '1': 'Poesía',
  '2': 'Cuento Breve',
  '3': 'Fotografía',
  '4': 'Dibujo',
};

const normalizeContestText = (text) =>
  text
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n\n')
    .trim();

const contestParagraphs = normalizeContestText(BASES_Y_CONDICIONES_TEXTO)
  .split('\n\n')
  .map((paragraph) => paragraph.replace(/\n/g, ' ').trim())
  .filter(Boolean);

const findInContestText = (regex, fallback = '') =>
  normalizeContestText(BASES_Y_CONDICIONES_TEXTO).match(regex)?.[0] || fallback;

const fechaLimite = findInContestText(/17 DE JULIO DE 2026/i, '17 DE JULIO DE 2026');
const fechaPremiacion = findInContestText(/9 de octubre de 2026/i, '9 de octubre de 2026');
const telefono = findInContestText(/\(15\)\s*5064-6321/, '(15) 5064-6321');
const email = findInContestText(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i, 'periodicolaidea1915@gmail.com');
const portal = findInContestText(/www\.multimedioslaidea\.com\.ar/i, 'www.multimedioslaidea.com.ar');
const arancelAdultos = findInContestText(/\$\s*2\.500/, '$ 2.500');
const arancelSuscriptores = findInContestText(/\$\s*2\.000/, '$ 2.000');

const preguntasFrecuentes = [
  {
    key: '1',
    question: '¿Quiénes pueden participar y cuánto cuesta la inscripción?',
    answer: `Pueden participar Adultos (18 años en adelante), Adolescentes (12 a 17 años) y Niños (8 a 11 años). Los adultos abonan ${arancelAdultos} por trabajo y los suscriptores ${arancelSuscriptores}. Adolescentes y Niños participan sin cargo.`,
  },
  {
    key: '2',
    question: '¿Cuál es la fecha límite y cuándo se entregan los premios?',
    answer: `La recepción de los trabajos vence el ${fechaLimite}. Las medallas y diplomas se entregarán el ${fechaPremiacion}.`,
  },
  {
    key: '3',
    question: '¿Cuántos trabajos se pueden presentar y cómo deben enviarse?',
    answer: 'Cada participante puede presentar hasta 3 trabajos por disciplina. Deben ir firmados con seudónimo y acompañados por una hoja con los datos personales del autor. En varias categorías también se aceptan envíos en formato digital y por mail.',
  },
  {
    key: '4',
    question: '¿Dónde se puede pedir más información?',
    answer: `La consulta se puede hacer las 24 horas al teléfono ${telefono}, por mail a ${email} o a través del portal ${portal}.`,
  },
];

const Concurso = () => {
  const dispatch = useDispatch();
  const winningWorks = useSelector((state) => state.allWorks);
  const [activeTab, setActiveTab] = useState('0');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  useEffect(() => {
    if (activeTab && selectedSubcategory) {
      const category = CATEGORY_BY_TAB[activeTab] || null;
      if (!category) {
        return;
      }

      dispatch(getWinningWorksByCategory(category, selectedSubcategory)).then((data) => {
        if (data.length === 0) {
          Swal.fire({
            title: 'Sin trabajos',
            text: `No hay trabajos en la categoría ${category} y subcategoría ${selectedSubcategory}`,
            icon: 'info',
            confirmButtonText: 'OK',
          });
        }
      });
    }
  }, [activeTab, selectedSubcategory, dispatch]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedSubcategory(null);
  };

  const handleSubcategorySelect = (subcat) => {
    setSelectedSubcategory(subcat);
  };

  const renderSubcategories = (categoryKey) => {
    if (CATEGORY_BY_TAB[categoryKey]) {
      return (
        <div className="subcategory-options">
          <p>Elige una subcategoría:</p>
          <ul>
            <li onClick={() => handleSubcategorySelect('Adulto')}>Adulto</li>
            <li onClick={() => handleSubcategorySelect('Adolescente')}>Adolescente</li>
            <li onClick={() => handleSubcategorySelect('Niño')}>Niño</li>
          </ul>
        </div>
      );
    }
    return null;
  };

  const renderWinningWorks = () =>
    winningWorks.map((work) => (
      <div key={work.id} className="work-item">
        <h3>{work.title}</h3>
        <img src={work.imageUrl} alt={work.title} className="work-image" />
        <p>{work.description}</p>
      </div>
    ));

  const renderBasesYCondiciones = () => (
    <div className="concurso-content">
      <div className="bases-highlight">
        <span className="bases-highlight-badge">Información principal</span>
        <h2 className="section-title">Bases y Condiciones</h2>
        <p className="bases-highlight-text">
          Esta es la información principal del Concurso Nacional. Incluye requisitos, fechas, costos, formas de envío y datos de contacto.
        </p>
      </div>
      <div className="bases-wrapper">
        {contestParagraphs.map((paragraph, index) => (
          <p key={`${index}-${paragraph.slice(0, 30)}`} className="bases-paragraph">
            {paragraph}
          </p>
        ))}
      </div>
      <h3 className="section-title">Preguntas Frecuentes</h3>
      <Collapse>
        {preguntasFrecuentes.map((item) => (
          <Panel header={item.question} key={item.key}>
            <p>{item.answer}</p>
          </Panel>
        ))}
      </Collapse>
    </div>
  );

  return (
    <div className="concurso-container">
      <Layout>
        <Content>
          <Row justify="center">
            <Col span={16} className="tabs-container">
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                centered
                className="concurso-tabs"
              >
                <TabPane tab="Bases y condiciones" key="0">
                  {renderBasesYCondiciones()}
                </TabPane>
                <TabPane tab="Poesía" key="1">
                  {renderSubcategories('1')}
                  {selectedSubcategory && (
                    <div>
                      <p>Contenido de Poesía - {selectedSubcategory}...</p>
                      {renderWinningWorks()}
                    </div>
                  )}
                </TabPane>
                <TabPane tab="Cuento Breve" key="2">
                  {renderSubcategories('2')}
                  {selectedSubcategory && (
                    <div>
                      <p>Contenido de Cuento Breve - {selectedSubcategory}...</p>
                      {renderWinningWorks()}
                    </div>
                  )}
                </TabPane>
                <TabPane tab="Fotografía" key="3">
                  {renderSubcategories('3')}
                  {selectedSubcategory && (
                    <div>
                      <p>Contenido de Fotografía - {selectedSubcategory}...</p>
                      {renderWinningWorks()}
                    </div>
                  )}
                </TabPane>
                <TabPane tab="Dibujo" key="4">
                  {renderSubcategories('4')}
                  {selectedSubcategory && (
                    <div>
                      <p>Contenido de Dibujo - {selectedSubcategory}...</p>
                      {renderWinningWorks()}
                    </div>
                  )}
                </TabPane>
              </Tabs>
            </Col>
          </Row>

          <div className="header-image">
            <img src={collageConcurso} alt="Concurso Nacional" className="large-image" />
          </div>
        </Content>
      </Layout>
    </div>
  );
};

export default Concurso;
